import {
  buildSSRSearchParameterSerializer,
  type SearchParameters,
} from '@coveo/headless-react/ssr';

const PLACEHOLDER_ORIGIN = 'http://localhost';

/**
 * Build the href of each single-select facet value: the URL a click on it
 * reaches. Serialized from the request parameters onto the current location the
 * same way SearchEngineProvider writes the URL after a selection, so the link
 * and the click agree — `firstResult` included, which a facet selection resets
 * and the serializer then omits. Non-Coveo params of the current location
 * (`hideOwnership`, `aff`, …) are kept, since the serializer only rewrites the
 * Coveo keys.
 *
 * Hrefs are relative (no origin, no fragment), so the server and the client
 * render the same attribute whatever the host, and an in-page anchor never
 * travels with the link. A `null` value is the active row's href: it clears
 * the facet.
 */
export function createSingleSelectFacetHrefBuilder({
  parameters,
  facetId,
  pathname,
  search,
}: {
  parameters: SearchParameters;
  facetId: string;
  pathname: string;
  search: string;
}): (value: string | null) => string {
  const { firstResult: _firstResult, f, ...rest } = parameters;
  const otherFacets = Object.fromEntries(
    Object.entries(f ?? {}).filter(([id]) => id !== facetId)
  );
  const query = search.replace(/^\?/, '');
  const base = query ? `${pathname}?${query}` : pathname;
  const { serialize } = buildSSRSearchParameterSerializer();

  return (value) => {
    const nextFacets =
      value === null ? otherFacets : { ...otherFacets, [facetId]: [value] };
    const nextParameters: SearchParameters =
      Object.keys(nextFacets).length > 0 ? { ...rest, f: nextFacets } : rest;
    const url = new URL(
      serialize(nextParameters, new URL(base, PLACEHOLDER_ORIGIN))
    );
    return `${url.pathname}${url.search}`;
  };
}
